import type {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  RefObject,
} from 'react';
import type { PinContentFn } from './PinContent';
import type { MenuItemChildProps } from './SectionMenuItemChild';

import {
  createRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { CaretDownIcon } from '../../images/shapes';

import { popoverUIMenuClasses } from '../Popover/classes';
import { baseUIPopoverItemClasses } from '../Popover/PopoverItem/PopoverItem';
import Tooltip from '../Tooltip/Tooltip';
import { SectionMenuItemChild } from './SectionMenuItemChild';

/**
 * Outside Listener event to close popover when clicked beyond open element
 */
const useOutsideListener = (
  ref: RefObject<HTMLElement | null>,
  handler?: () => void,
  hasEventOnCloseOutside?: boolean,
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref && ref.current && !ref.current.contains(event.target as Node)) {
        handler?.();
      }
    };

    if (hasEventOnCloseOutside)
      document.addEventListener('mousedown', handleClickOutside);

    return () => {
      if (hasEventOnCloseOutside)
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler, hasEventOnCloseOutside]);
};

interface MenuItemProps {
  /**
   * Link to redirect on click
   */
  link?: string;
  /**
   * Target for the link
   */
  linkTarget?: string;
  /**
   * Rel for the link
   */
  linkRel?: string;
  /**
   * On click handler
   */
  onClick?: (e: ReactMouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /**
   * If menu item is open
   */
  isOpen?: boolean;
  /**
   * Children to display inside menu item
   */
  children?: ReactNode;
  /**
   * Title of the menu item
   */
  title?: ReactNode;
  /**
   * Tooltip for the menu item
   */
  tooltip?: ReactNode;
  /**
   * If menu item is disabled
   */
  disabled?: boolean;
  /**
   * Test id for the menu item
   */
  testId?: string;
}

const MenuItem = ({
  link,
  linkTarget,
  linkRel,
  onClick,
  isOpen,
  children,
  title,
  tooltip,
  disabled,
  testId,
}: MenuItemProps) => {
  const itemContainerClasses = twMerge(
    'flex h-full pl-5 pr-3 items-center dark:decoration-white user-drag-none text-left',
    isOpen && 'w-full',
    disabled && 'cursor-not-allowed opacity-50',
  );

  const linkRef = useRef<HTMLAnchorElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRef = link ? linkRef : buttonRef;

  const [currentTop, setCurrentTop] = useState<number | undefined>();

  const handlePosition = useCallback(() => {
    setCurrentTop(itemRef.current?.getBoundingClientRect()?.top);
  }, [itemRef]);

  const resetCurrentTop = useCallback(() => {
    setCurrentTop(undefined);
  }, []);

  const handleClick = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (disabled) return;
      onClick?.(e);
    },
    [disabled, onClick],
  );

  const portalTooltip =
    (!isOpen || tooltip) &&
    currentTop &&
    title &&
    createPortal(
      <div
        className="fixed top-0 left-0 z-90 pointer-events-none"
        style={{
          top: currentTop,
        }}
      >
        <Tooltip
          tooltip={tooltip || title}
          tooltipPlacement={!isOpen ? 'rightCenter' : 'topCenter'}
          phoneTooltipPlacement={!isOpen ? 'rightCenter' : 'topCenter'}
          additionalTooltipClasses={twMerge(
            'group-focus/tooltip:invisible! group-active/tooltip:invisible!',
            'visible! opacity-100! max-w-52!',
          )}
        >
          <div className={twMerge(!isOpen ? 'w-16' : 'w-60', 'h-10')} />
        </Tooltip>
      </div>,
      document.body,
    );

  if (link)
    return (
      <Link
        ref={linkRef}
        className={itemContainerClasses}
        to={link}
        onClick={handleClick}
        target={linkTarget}
        rel={linkRel}
        onMouseEnter={handlePosition}
        onMouseLeave={resetCurrentTop}
        onFocus={resetCurrentTop}
        {...getTestProps(testId, 'link')}
      >
        {children}
        {portalTooltip}
      </Link>
    );

  return (
    <button
      ref={buttonRef}
      className={itemContainerClasses}
      onClick={handleClick}
      onMouseEnter={handlePosition}
      onMouseLeave={resetCurrentTop}
      onFocus={resetCurrentTop}
      {...getTestProps(testId, 'div')}
    >
      {children}
      {portalTooltip}
    </button>
  );
};

interface SectionMenuItemProps {
  /**
   * Menu Items
   */
  menuItems?: MenuItemChildProps[];
  /**
   * If we want to open multiple parent at once.
   */
  isListOpenSingle?: boolean;
  /**
   * Name of the title that we want to select
   */
  selected?: string;
  /**
   * If sidebar is opened
   */
  isOpen?: boolean;
  /**
   * Handler that will work only for isOpen:false to trigger func to be able to open sidebar
   */
  handleParentOpen?: (isOpen: boolean) => void;
  /**
   * Name of the Title that should be open if they have children
   */
  openItem?: string;
  /**
   * Separator that is used to help validate if `selected` string could be with any separator.
   */
  selectedSeparator?: string;
  /**
   * Parent key for children elements
   */
  parent?: string;
  /**
   * If pin should be hidden for children
   */
  hidePin?: boolean;
  /**
   * Callback to pin or unpin content
   */
  pinContentCallback?: PinContentFn;
  /**
   * Additional Menu Item Classes
   */
  additionalMenuItemClasses?: string;
  /**
   * Additional Menu Item Icon Classes
   */
  additionalMenuItemIconClasses?: string;
  /**
   * Additional Menu Item Child Classes
   */
  additionalMenuItemChildClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

export const SectionMenuItem = ({
  menuItems,
  isListOpenSingle = true,
  selected = '',
  isOpen = true,
  handleParentOpen,
  openItem = '',
  selectedSeparator = '/',
  parent = '',
  additionalMenuItemClasses,
  additionalMenuItemIconClasses,
  additionalMenuItemChildClasses,
  pinContentCallback,
  hidePin = false,
  testId,
}: SectionMenuItemProps) => {
  const [open, setOpen] = useState({ [openItem]: true });
  const [currentKeyId, setCurrentKeyId] = useState<string>('');
  const refsById = useMemo(() => {
    const refs: { [key: string]: RefObject<HTMLUListElement | null> } = {};
    menuItems?.forEach((item) => {
      if (item.popover) {
        refs[item.key] = createRef();
      }
    });
    return refs;
  }, [menuItems]);

  useOutsideListener(
    refsById[currentKeyId],
    () => setOpen({ ...open, [currentKeyId]: false }),
    Object.keys(refsById).length > 0,
  );

  useEffect(() => {
    if (openItem) {
      setOpen({ [openItem]: true });
    } else {
      setOpen({});
    }
  }, [openItem]);

  const handleOpenList = (key: string, popover?: boolean) => {
    const openListStatus = {
      [key]: open[key] === undefined ? true : !open[key],
    };
    setCurrentKeyId(key);

    if (isListOpenSingle && !popover) {
      setOpen(openListStatus);
    } else {
      setOpen({
        ...open,
        ...openListStatus,
      });
    }

    // Case: Open menu when onClick we open child list
    if (!isOpen && handleParentOpen) {
      handleParentOpen(true);
    }
  };

  const handleClickInPopover = useCallback(
    (popover?: boolean, key: string = '') => {
      if (popover) {
        setOpen({
          ...open,
          [key]: false,
        });
      }
    },
    [open],
  );

  return (
    <ul {...getTestProps(testId, 'container')}>
      {menuItems?.map((item) => {
        const isSelected = selected?.startsWith(
          parent
            ? parent + selectedSeparator + item.key + selectedSeparator
            : item.key,
        );

        const isOpenable = item?.popover
          ? item?.children && item.children.length > 1
          : item?.children && item.children.length > 0;

        return (
          <Fragment key={'menu-item' + item.key}>
            <li
              className={twMerge(
                'group flex w-full relative h-10 mb-1.5 text-xl  ',
                !item?.disabled &&
                  'cursor-pointer hover:bg-gradient-menu-hover',
                isSelected &&
                  !item?.children?.length &&
                  'border-blue border-r-4 bg-gradient-menu-active dark:bg-gradient-dark-menu-active',
                typeof item.title === 'string' &&
                  item.title.length > 20 &&
                  'h-12',
                item.popover &&
                  isOpen &&
                  open[item.key] &&
                  'pointer-events-none',
                item.className,
                additionalMenuItemClasses,
              )}
              {...(isOpenable && {
                onClick: () => handleOpenList(item.key),
              })}
              {...getTestProps(testId, item.key)}
            >
              <MenuItem
                link={item?.link}
                linkTarget={item?.target}
                linkRel={item?.rel}
                title={item?.title}
                onClick={item?.onClick}
                isOpen={isOpen}
                tooltip={item?.tooltip}
                disabled={item?.disabled}
                {...getTestProps(testId, item.key, 'testId')}
              >
                {item.icon}

                <div
                  className={twMerge(
                    'whitespace-pre-wrap',
                    'overflow-hidden ml-3',
                    'transition-all ease-linear',
                    'duration-normal text-base',
                    'line-clamp-2 leading-normal font-medium py-0.5 dark:text-white dark:decoration-white',
                    item?.children && item.children.length > 0 && 'mr-8',
                    !isOpen && 'w-0',
                    item?.titleClassName,
                  )}
                  title={
                    typeof item.title === 'string' && item.title.length > 20
                      ? item.title
                      : undefined
                  }
                  {...getTestProps(testId, `title-${item.key}`)}
                >
                  {item.title}
                </div>

                {isOpenable && isOpen && (
                  <CaretDownIcon
                    className={twMerge(
                      'absolute right-4 w-3',
                      'select-none transition-all',
                      'ease-linear duration-normal text-slate-900 dark:text-white',
                      open[item.key] && 'rotate-180',
                      additionalMenuItemIconClasses,
                    )}
                    {...getTestProps(testId, `arrow-down-${item.key}`)}
                  />
                )}
              </MenuItem>
            </li>

            {item?.children && item.children.length > 0 && (
              <li className="relative">
                <SectionMenuItemChild
                  ref={refsById[item.key]}
                  parent={
                    parent ? parent + selectedSeparator + item.key : item.key
                  }
                  items={item.children}
                  selected={selected}
                  selectedSeparator={selectedSeparator}
                  pinContentCallback={pinContentCallback}
                  hidePin={hidePin || item.hidePin}
                  show={isOpen && open[item.key]}
                  handleParentOpen={() => handleOpenList(item.key)}
                  additionalMenuItemChildClasses={twMerge(
                    'pl-0',
                    additionalMenuItemChildClasses,
                    item.popover && 'hover:bg-none',
                  )}
                  additionalMenuItemChildContainerClasses={twMerge(
                    item.popover &&
                      'pb-0 absolute z-10 h-0 left-5 w-[205px] max-w-[205px] ' +
                        `${
                          isOpen && open[item.key]
                            ? 'h-auto ' + popoverUIMenuClasses
                            : ''
                        }`,
                  )}
                  additionalMenuItemChildInnerClasses={twMerge(
                    item.popover &&
                      baseUIPopoverItemClasses + ' h-auto 2xl:h-auto',
                    item.popover && !item.hidePin && 'pl-14',
                  )}
                  onClick={() => handleClickInPopover(item.popover, item.key)}
                  {...getTestProps(testId, 'child', 'testId')}
                />
              </li>
            )}
          </Fragment>
        );
      })}
    </ul>
  );
};
